/**
 * The words of an answer's receipt (Phase C): what the tools actually read,
 * from the server's `contextReceipt` (backend answer_receipt.py), in the
 * context bar's words. Pure, so the receipt, its tests and the bar never
 * say different things.
 */
import i18n from '../../../i18n';
import { sourceMeta } from '../data/reservationSources';
import type { AnswerContextReceipt, ReceiptFilterPart, ReceiptWindow, ReservationSourceId, ReservationStatusGroup } from '../types';
import { RESERVATION_STATUS_GROUPS } from '../types';
import {
  definitionChip,
  formatRange,
  listJoin,
  notUsedNote,
  ownWindowText,
  shortList,
  shiftsText,
  sourcesText,
  statusesText,
  venuesText,
} from './contextSummary';
import { PERF_COMPARE_KIND_OF, type RequestViewContext } from './viewContext';

const t = (key: string, opts?: Record<string, unknown>) => i18n.t(`assistant:receipt.${key}`, opts) as string;
const chrome = (key: string, opts?: Record<string, unknown>) => i18n.t(`chrome:context.${key}`, opts) as string;

export type ReceiptRow = {
  key: 'venues' | 'own' | 'period' | 'comparison' | 'statuses' | 'sources' | 'shifts' | 'definitions' | 'dates' | 'outside';
  label: string;
  value: string;
  /** What didn't apply or came from the question; shown beside the value. */
  note?: string;
  /** A filter the person set that some figures didn't follow. */
  flag?: boolean;
  /** A comparison window ("vs …" in the collapsed line). */
  vs?: boolean;
};

const STATUS_GROUP_IDS = new Set(RESERVATION_STATUS_GROUPS.map(g => g.id));

function statusValue(value: string): string {
  if (STATUS_GROUP_IDS.has(value as ReservationStatusGroup)) {
    return RESERVATION_STATUS_GROUPS.find(g => g.id === value)?.label ?? value;
  }
  // The count tools' own status checkboxes (their `statuses`).
  return i18n.t(`assistant:countReceipt.status.${value}`, { defaultValue: value }) as string;
}

function familyNoun(family: string): string {
  return t(`family.${family}`, { defaultValue: t('family.reservations') });
}

function partValue(part: 'statuses' | 'sources' | 'shifts', values: string[]): string {
  if (part === 'statuses') {
    if (!values.length) return chrome('filters.allStatuses');
    if (values.every(v => STATUS_GROUP_IDS.has(v as ReservationStatusGroup))) {
      return statusesText(values as ReservationStatusGroup[]);
    }
    return chrome('filters.statuses', { list: shortList(values.map(statusValue)) });
  }
  if (part === 'sources') return sourcesText(values as ReservationSourceId[]);
  return shiftsText(values);
}

function questionText(part: 'statuses' | 'sources' | 'shifts', entry: { values: string[]; exclude?: boolean }): string {
  const names =
    part === 'statuses'
      ? entry.values.map(statusValue)
      : part === 'sources'
        ? entry.values.map(v => sourceMeta(v as ReservationSourceId).label)
        : entry.values;
  if (!names.length) return partValue(part, []);
  return entry.exclude ? t(`except.${part}`, { list: shortList(names) }) : shortList(names);
}

function filterRow(part: 'statuses' | 'sources' | 'shifts', receipt: ReceiptFilterPart, notUsed: string): ReceiptRow {
  const notes: string[] = [];
  let value: string;
  if (receipt.question?.length && receipt.applied.length) {
    // Some figures followed the context's filter, others the question's:
    // both are named ("Statuses: Done · and Cancelled, as asked in the question").
    value = partValue(part, receipt.applied);
    notes.push(t('alsoAsked', { list: receipt.question.map(entry => questionText(part, entry)).join('; ') }));
  } else if (receipt.question?.length) {
    value = receipt.question.map(entry => questionText(part, entry)).join('; ');
    notes.push(t('asked'));
  } else if (receipt.applied.length) {
    value = partValue(part, receipt.applied);
  } else {
    value = partValue(part, receipt.chosen);
  }
  const tool = receipt.notApplied?.tool ?? [];
  const screen = receipt.notApplied?.screen ?? [];
  if (tool.length) notes.push(t('notApplied', { by: listJoin(tool.map(familyNoun)) }));
  if (screen.length && notUsed) notes.push(notUsed);
  return {
    key: part,
    label: t(`label.${part}`),
    value,
    ...(notes.length ? { note: notes.join(' · ') } : {}),
    ...(tool.length || screen.length ? { flag: true } : {}),
  };
}

function rangesOf(windows: ReceiptWindow[]): string {
  const seen: string[] = [];
  for (const w of windows) {
    const text = formatRange(w.start, w.end);
    if (text && !seen.includes(text)) seen.push(text);
  }
  return seen.join(', ');
}

function comparisonKindText(context: RequestViewContext | null | undefined): string {
  const kind = context?.comparison?.kind;
  if (!kind || kind === 'none') return '';
  return chrome(`comparison.kind.${PERF_COMPARE_KIND_OF[kind]}`);
}

/**
 * The rows of a receipt: venues, the windows read (the screen's own first),
 * the comparison, the three filters, the definitions, the date basis, and
 * anything refused as outside the conversation's context.
 */
export function receiptRows(receipt: AnswerContextReceipt, context: RequestViewContext | null | undefined): ReceiptRow[] {
  const rows: ReceiptRow[] = [];
  const venues = receipt.venues;
  const venueValue =
    venues.matchesContext && context
      ? venuesText(context)
      : venues.names?.length
        ? shortList(venues.names, 3)
        : chrome('venues.count', { count: venues.count });
  if (venues.count || context) rows.push({ key: 'venues', label: t('label.venues'), value: venueValue });

  const own = receipt.windows.filter(w => w.role === 'own');
  for (const window of own) {
    const kind = window.kind === 'live' || window.implicit === 'live' ? 'live' : 'forecast';
    const label = kind === 'live' ? t('label.live') : t('label.forecast');
    const text =
      ownWindowText({
        kind,
        start: window.start,
        end: window.end,
        label: kind === 'forecast' ? (context?.window?.label ?? null) : null,
        key: kind === 'forecast' ? (context?.window?.key ?? null) : null,
      }) ?? formatRange(window.start, window.end);
    if (!rows.some(r => r.key === 'own' && r.value === text)) {
      rows.push({ key: 'own', label, value: text, note: t('ownWindow') });
    }
  }
  const periods = receipt.windows.filter(w => w.role === 'period' || w.role === 'within' || w.role === 'focus');
  if (periods.length) {
    const narrower = periods.every(w => w.role !== 'period');
    rows.push({
      key: 'period',
      label: t('label.period'),
      value: rangesOf(periods),
      ...(narrower ? { note: t('withinPeriod') } : {}),
    });
  }
  const compared = receipt.windows.filter(w => w.role === 'comparison');
  if (compared.length) {
    const kind = comparisonKindText(context);
    rows.push({
      key: 'comparison',
      label: t('label.comparison'),
      value: rangesOf(compared),
      vs: true,
      ...(kind ? { note: kind } : {}),
    });
  } else if (receipt.ownComparison?.length) {
    rows.push({
      key: 'comparison',
      label: t('label.comparison'),
      value: listJoin(receipt.ownComparison.map(kind => t(`ownComparison.${kind}`, { defaultValue: t('ownComparison.baseline') }))),
    });
  } else if (context?.uses?.comparison && context.comparison?.kind && context.comparison.kind !== 'none') {
    rows.push({ key: 'comparison', label: t('label.comparison'), value: t('comparisonNotRead') });
  }

  // The bar's words for where a filter isn't used ("not used on Forecast").
  const notUsed = context ? notUsedNote(context.screen) : '';
  rows.push(filterRow('statuses', receipt.statuses, notUsed));
  rows.push(filterRow('sources', receipt.sources, notUsed));
  rows.push(filterRow('shifts', receipt.shifts, notUsed));

  if (receipt.definitions.length) {
    // The bar's chip words for the same key (definitionChip), so "How it
    // counts" says what the screen's definitions chip says for this context.
    const statuses = receipt.statuses.applied.filter((v): v is ReservationStatusGroup =>
      STATUS_GROUP_IDS.has(v as ReservationStatusGroup),
    );
    rows.push({
      key: 'definitions',
      label: t('label.definitions'),
      value: receipt.definitions
        .map(key => definitionChip(key, statuses) ?? t(`definition.${key}`, { defaultValue: key }))
        .join(' · '),
    });
  }
  if (receipt.dateBasis === 'creation') {
    rows.push({ key: 'dates', label: t('label.dates'), value: t('creationBasis') });
  }
  if (receipt.outOfContext?.length) {
    const needs: string[] = [];
    for (const item of receipt.outOfContext) {
      for (const d of item.dates) needs.push(formatRange(d.start, d.end));
      for (const v of item.venues) needs.push(v.name);
    }
    rows.push({
      key: 'outside',
      label: t('label.outside'),
      value: [...new Set(needs)].join(', '),
      note: t('outsideNote'),
      flag: true,
    });
  }
  return rows;
}

/**
 * The collapsed receipt in one line: "Seefeld · 14 Sept – 11 Oct 2026 · vs
 * 15 Sept – 12 Oct 2025 · Statuses: Done · all sources · all shifts".
 */
export function receiptSummary(rows: ReceiptRow[]): { text: string; flagged: number; outside: boolean } {
  const pick = (key: ReceiptRow['key']) => rows.find(r => r.key === key);
  const parts: string[] = [];
  const venues = pick('venues');
  if (venues) parts.push(venues.value);
  const own = rows.filter(r => r.key === 'own').map(r => r.value);
  parts.push(...own);
  const period = pick('period');
  if (period) parts.push(period.value);
  const comparison = pick('comparison');
  if (comparison?.vs) parts.push(chrome('comparison.vs', { range: comparison.value }));
  else if (comparison && comparison.value !== t('comparisonNotRead')) parts.push(comparison.value);
  const filters = (['statuses', 'sources', 'shifts'] as const)
    .map(key => pick(key)?.value)
    .filter((v): v is string => Boolean(v));
  parts.push(...filters.map((text, i) => (i > 0 && isAll(text) ? lowerFirstWord(text) : text)));
  return {
    text: parts.join(' · '),
    flagged: rows.filter(r => r.flag && (r.key === 'statuses' || r.key === 'sources' || r.key === 'shifts')).length,
    outside: rows.some(r => r.key === 'outside'),
  };
}

function isAll(text: string): boolean {
  return [chrome('filters.allStatuses'), chrome('filters.allSources'), chrome('filters.allShifts')].includes(text);
}

function lowerFirstWord(text: string): string {
  return i18n.language?.startsWith('de') ? text.replace(/^Alle /, 'alle ') : text.charAt(0).toLowerCase() + text.slice(1);
}
